import useStyles from "../styles/styles";
import {useMutation, useQuery} from "@apollo/react-hooks";
import {gql} from "apollo-boost";
import {
    Box, Button, Chip,
    CircularProgress,
    Container,
    Grid, Modal,
    Paper, Table, TableBody, TableCell, TableHead, TableRow,
    Typography
} from "@material-ui/core";
import React, {useEffect, useState} from "react";
import {Alert} from "@material-ui/lab";
import useTrafficStyles from "../styles/trafficStyles";
import useFormInputs from "../hooks/useFormInputs";

const FrontendConfig = () => {
    const classes = useStyles();
    const trafficClasses = useTrafficStyles();

    const [conflicts, setConflicts] = useState([]);
    const [duplicates, setDuplicates] = useState([]);
    const [operations, setOperations] = useState([]);
    const [canSynchronize, setCanSynchronize] = useState(false);
    const [isResultModalOpen, setIsResultModalOpen] = useState(false);
    const [isMutationErrorModalOpen, setIsMutationErrorModalOpen] = useState(false);

    const onSubmit = () => {
        setConflicts([]);
        setDuplicates([]);
        setOperations([]);
        setCanSynchronize(false);
        synchronizeConfigs();
    };

    const {handleSubmit} = useFormInputs(onSubmit);
    const {loading, error, data, refetch} = useQuery(gql`
        query FrontendConfig {
            checkConfigs {
                conflicts {
                    configWithLocation {
                        config {
                            id
                            tag
                        }
                        location {
                            control
                            controlField
                            formFlow
                            formStep
                        }
                        label
                    }
                    location {
                        control
                        controlField
                        formFlow
                        formStep
                        label
                    }
                    reasons
                }
            }
            getAllSyncOperations {
                inSync
                key
                operations {
                    description
                }
            }
            reduceConfigs {
                duplicateConfig
            }
        }
    `, {
        notifyOnNetworkStatusChange: true,
    });

    const [synchronizeConfigs, {data: mutationData, error: mutationError, loading: mutationLoading}] = useMutation(gql`
        mutation SynchronizeConfigs {
            synchronizeConfigs
        }
    `);

    const updateConflicts = (data) => {
        let conflicts = [];

        data.checkConfigs.conflicts.forEach((nextConflict) => {
            nextConflict.reasons.forEach((nextReason) => {
                conflicts.push({
                    configWithLocation: nextConflict.configWithLocation ? (nextConflict.configWithLocation.label) : (''),
                    location: nextConflict.location.label,
                    reason: nextReason
                });
            });
        });

        setConflicts(conflicts);
    };

    const updateDuplicates = (data) => {
        let duplicates = [];

        data.reduceConfigs.forEach((nextDuplicate) => {
            duplicates.push({
                duplicateConfig: nextDuplicate.duplicateConfig
            });
        });

        setDuplicates(duplicates);
    };

    const updateOperations = (data) => {
        let operations = [];

        data.getAllSyncOperations.forEach((nextConfigSyncOperations) => {
            nextConfigSyncOperations.operations.forEach((nextSyncOperation) => {
                operations.push({
                    key: nextConfigSyncOperations.key,
                    operation: nextSyncOperation.description
                });
            });
        });

        setOperations(operations);
    };

    useEffect(() => {
        if (mutationData && mutationData.synchronizeConfigs) {
            setIsResultModalOpen(true);
        }
    }, [mutationData]);

    useEffect(() => {
        if (!mutationLoading && mutationError) {
            setIsMutationErrorModalOpen(true);
        }
    }, [mutationError])

    useEffect(() => {
        if (data && data.checkConfigs && data.checkConfigs.conflicts) {
            updateConflicts(data)
        }

        if (data && data.reduceConfigs) {
            updateDuplicates(data)
        }

        if (data && data.getAllSyncOperations) {
            updateOperations(data)
        }
    }, [data]);

    useEffect(() => {
        if (conflicts.length === 0 && operations.length > 0) {
            setCanSynchronize(true);
        }
    }, [conflicts, operations]);

    const NoConflicts = () => {
        return(
            <span>No Conflicts</span>
        );
    }

    const ConflictTableRow = ({index, configWithLocation, location, reason}) => {
        return (
            <TableRow key={`conflict-${index}`}>
                <TableCell variant="body">{configWithLocation}</TableCell>
                <TableCell variant="body">{location}</TableCell>
                <TableCell variant="body">{reason}</TableCell>
            </TableRow>
        );
    }

    const ConflictsTable = (data) => {
        return (
            <Table className={classes.table} size="small">
                <TableHead>
                    <TableRow>
                        <TableCell variant="head">Config W/ Location</TableCell>
                        <TableCell variant="head">Conflict Location</TableCell>
                        <TableCell variant="head">Reason</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.conflicts.map((conflict, index) => {
                        return (
                            <ConflictTableRow
                                index={index}
                                configWithLocation={conflict.configWithLocation}
                                location={conflict.location}
                                reason={conflict.reason}
                            />
                        );
                    })}
                </TableBody>
            </Table>
        );
    }

    const ConflictsView = (data) => {
        return (
            <Box className={classes.filterBox} style={{marginTop: 0}}>
                <Chip
                    label="Conflicts"
                    className={classes.filterSectionLabels}
                />
                {
                    data.conflicts.length === 0 ? (
                        <NoConflicts/>
                    ) : (
                        <ConflictsTable
                            conflicts={data.conflicts}
                        />
                    )
                }
            </Box>
        );
    };

    const NoOperations = () => {
        return (
            <span>No Operations</span>
        );
    }

    const OperationTableRow = ({index, operationKey, operation}) => {
        return (
            <TableRow key={`operation-${index}`}>
                <TableCell variant="body">{operationKey}</TableCell>
                <TableCell variant="body">{operation}</TableCell>
            </TableRow>
        );
    }

    const OperationsTable = (data) => {
        return (
            <Table className={classes.table} size="small">
                <TableHead>
                    <TableRow>
                        <TableCell variant="head">Key</TableCell>
                        <TableCell variant="head">Operation</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.operations.map((operation, index) => {
                        return (
                            <OperationTableRow
                                index={index}
                                operationKey={operation.key}
                                operation={operation.operation}
                            />
                        );
                    })}
                </TableBody>
            </Table>
        );
    }

    const OperationsView = (data) => {
        return (
            <Box className={classes.filterBox} style={{marginTop: 0}}>
                <Chip
                    label="Operations"
                    className={classes.filterSectionLabels}
                />
                {
                    data.operations.length === 0 ? (
                        <NoOperations/>
                    ) : (
                        <OperationsTable
                            operations={data.operations}
                        />
                    )
                }
            </Box>
        );
    };

    const NoDuplicates = () => {
        return (
            <span>No Duplicates</span>
        );
    }

    const DuplicateTableRow = ({index, duplicateConfig}) => {
        return (
            <TableRow key={`duplicate-${index}`}>
                <TableCell variant="body">{duplicateConfig}</TableCell>
            </TableRow>
        );
    }

    const DuplicatesTable = (data) => {
        return (
            <Table className={classes.table} size="small">
                <TableHead>
                    <TableRow>
                        <TableCell variant="head">Duplication</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.duplicates.map((duplicate, index) => {
                        return (
                            <DuplicateTableRow
                                index={index}
                                duplicateConfig={duplicate.duplicateConfig}
                            />
                        );
                    })}
                </TableBody>
            </Table>
        );
    }

    const ConfigReductionView = (data) => {
        return (
            <Box className={classes.filterBox} style={{marginTop: 0}}>
                <Chip
                    label="Duplicate Configs"
                    className={classes.filterSectionLabels}
                />
                {
                    data.duplicates.length === 0 ? (
                        <NoDuplicates/>
                    ) : (
                        <DuplicatesTable
                            duplicates={data.duplicates}
                        />
                    )
                }
            </Box>
        );
    };


    const SyncResultTableRow = ({index, operation}) => {
        return (
            <TableRow key={`operation-${index}`}>
                <TableCell variant="body">{operation}</TableCell>
            </TableRow>
        );
    }

    const SyncResultTable = (data) => {
        return (
            <Table className={classes.table} size="small">
                <TableHead>
                    <TableRow>
                        <TableCell variant="head">Operation</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.operations.map((operation, index) => {
                        return (
                            <SyncResultTableRow
                                index={index}
                                operation={operation}
                            />
                        );
                    })}
                </TableBody>
            </Table>
        );
    }

    const SyncResultView = (data) => {
        return (
            <Box className={classes.filterBox} style={{marginTop: 0}}>
                <Chip
                    label="Operations"
                    className={classes.filterSectionLabels}
                />
                {
                    data.operations.length === 0 ? (
                        <NoOperations/>
                    ) : (
                        <SyncResultTable
                            operations={data.operations}
                        />
                    )
                }
            </Box>
        );
    };

    const SyncResultModal = () => {
        return (
            <Modal open={isResultModalOpen}>
                <Container className={`${classes.modal} ${trafficClasses.modal}`} maxWidth={false}>
                    <Paper className={classes.paper} style={{marginBottom: 0}}>
                        <Typography variant="h5" align="center" gutterBottom>
                            Synchronization Operations Executed
                        </Typography>
                        {
                            mutationData ? (
                                <SyncResultView
                                    operations={mutationData.synchronizeConfigs}
                                />
                            ) : (
                                <></>
                            )
                        }
                        <Button variant="outlined" onClick={() => setIsResultModalOpen(false)}>
                            Close
                        </Button>
                    </Paper>
                </Container>
            </Modal>
        );
    }

    const closeMutationErrorModal = () => {
        setIsMutationErrorModalOpen(false);
        refetch();
    };

    const MutationErrorModal = () => {
        return (
            <Modal open={isMutationErrorModalOpen}>
                <Container className={`${classes.modal} ${trafficClasses.modal}`} maxWidth={false}>
                    <Paper className={classes.paper} style={{marginBottom: 0}}>
                        <Alert severity="error">There is an error!</Alert>
                        <pre>{JSON.stringify(mutationError, null, 2)}</pre>
                        <Button variant="outlined" onClick={() => closeMutationErrorModal()}>
                            Close
                        </Button>
                    </Paper>
                </Container>
            </Modal>
        );
    }

    const FrontendConfigView = () => {
        return (
            <>
                <Box className={trafficClasses.headerBox}>
                    <Grid container alignItems="flex-end">
                        <Grid item xs={12} style={{minHeight: '100%'}}>
                            <ConflictsView
                                conflicts={conflicts}
                            />
                        </Grid>
                    </Grid>
                </Box>
                <Box className={trafficClasses.headerBox}>
                    <Grid container alignItems="flex-end">
                        <Grid item xs={12} style={{minHeight: '100%'}}>
                            <ConfigReductionView
                                duplicates={duplicates}
                            />
                        </Grid>
                    </Grid>
                </Box>
                <Box className={trafficClasses.headerBox}>
                    <Grid container alignItems="flex-end">
                        <Grid item xs={12} style={{minHeight: '100%'}}>
                            <OperationsView
                                operations={operations}
                            />
                        </Grid>
                    </Grid>
                </Box>

                <form id="frontend-config-form" onSubmit={handleSubmit}>
                    <Button type="submit" variant="contained" color="secondary" fullWidth disabled={!canSynchronize}>
                        Synchronize Configs
                    </Button>
                </form>

                <SyncResultModal/>
            </>
        );
    };

    return (
        <>
            <Typography variant="h2">
                Frontend Config
            </Typography>
            {!loading && error ? (
                <Paper className={classes.paper}>
                    <Alert severity="error">There is an error!</Alert>
                    <pre>{JSON.stringify(error, null, 2)}</pre>
                </Paper>
            ) : null}
            {!mutationLoading && mutationError ? (
                <MutationErrorModal/>
            ) : null}
            {loading || mutationLoading ? (
                <Paper className={classes.paper}>
                    <div className={classes.loadingBar}>
                        <CircularProgress
                            size={50}
                            thickness={4}
                        />
                    </div>
                </Paper>
            ) : null}
            {!loading && !mutationLoading && !isMutationErrorModalOpen && data && data.checkConfigs ? (
                <Paper className={classes.paper}>
                    <Container maxWidth={false}>
                        <FrontendConfigView/>
                    </Container>
                </Paper>
            ) : null}
        </>
    );
}

export default FrontendConfig;